import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { StyledTextField, StyledFormControlLabel } from './StyledFormComponents';

declare global {
  interface Window {
    turnstile: any;
  }
}

const dataTypeOptions = [
  { value: 'account', label: 'My account and profile information' },
  { value: 'listening_history', label: 'My listening history' },
  { value: 'playlists', label: 'My playlists' },
  { value: 'payment_info', label: 'My payment information' },
  { value: 'all', label: 'Everything - delete all my data' },
];

const DeleteDataForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    reason: '',
    data_types: [] as string[],
    additional_info: '',
  });

  // Initialize Turnstile verification
  useEffect(() => {
    let attemptInterval: ReturnType<typeof setTimeout>;

    const attemptRenderTurnstile = () => {
      if (window.turnstile && turnstileContainerRef.current && !turnstileWidgetIdRef.current) {
        try {
          const widgetId = window.turnstile.render(turnstileContainerRef.current, {
            sitekey: '0x4AAAAAABfgu43iKR6cfJY3',
            callback: (token: string) => {
              setTurnstileToken(token);
            },
            'expired-callback': () => {
              setTurnstileToken('');
            },
            'error-callback': () => {
              setTurnstileToken('');
              setError('Verification challenge failed. Please try refreshing the page.');
            },
          });
          turnstileWidgetIdRef.current = widgetId;
          clearInterval(attemptInterval);
        } catch (renderError) {
          console.error('Turnstile render error:', renderError);
          setError('Could not load verification challenge. Please refresh.');
        }
      }
    };

    attemptRenderTurnstile();
    attemptInterval = setInterval(attemptRenderTurnstile, 1000);

    return () => {
      clearInterval(attemptInterval);
      if (window.turnstile && turnstileWidgetIdRef.current) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
        turnstileWidgetIdRef.current = null;
      }
    };
  }, []);

  const handleChange = (field: string, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleDataTypeToggle = (value: string) => {
    setForm((prev) => {
      const currentTypes = prev.data_types;
      if (value === 'all') {
        // If "all" is selected, clear everything else and only keep "all"
        return { ...prev, data_types: ['all'] };
      } else {
        // Remove "all" if another option is selected
        const filteredTypes = currentTypes.filter(t => t !== 'all');
        if (currentTypes.includes(value)) {
          return { ...prev, data_types: filteredTypes.filter(t => t !== value) };
        } else {
          return { ...prev, data_types: [...filteredTypes, value] };
        }
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!turnstileToken) {
      setError('Please complete the human verification step.');
      setLoading(false);
      return;
    }

    if (form.data_types.length === 0) {
      setError('Please select at least one type of data to delete.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/public/support/delete-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...form, 
          cfTurnstileToken: turnstileToken 
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Submission failed');
      }

      setSubmitted(true);
    } catch (err: any) {
      console.error('Submission error:', err);
      setError(err.message || 'Something went wrong.');
      if (window.turnstile && turnstileWidgetIdRef.current) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      }
      setTurnstileToken('');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h5" sx={{ fontFamily: '"Poppins", sans-serif', fontWeight: 700, mb: 2 }}>
          ✅ Data Deletion Request Submitted
        </Typography>
        <Typography sx={{ fontFamily: '"Poppins", sans-serif', opacity: 0.9 }}>
          Your request has been received. We will process it within 30 days and send you a confirmation email when complete.
        </Typography>
      </Box>
    );
  }

  const isButtonDisabled = loading || !turnstileToken;

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" sx={{ mb: 1, opacity: 0.8, fontFamily: '"Poppins", sans-serif' }}>
        We respect your privacy. Submit this form to request deletion of your data. This action cannot be undone.
      </Typography>

      <StyledTextField 
        label="Your Name" 
        required 
        value={form.name} 
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('name', e.target.value)} 
      />
      
      <StyledTextField 
        label="Your Email" 
        required 
        type="email" 
        value={form.email} 
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('email', e.target.value)} 
      />

      <Box sx={{ mt: 1 }}>
        <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600, fontFamily: '"Poppins", sans-serif' }}>
          What data would you like us to delete? *
        </Typography>
        <FormGroup>
          {dataTypeOptions.map((option) => (
            <StyledFormControlLabel
              key={option.value}
              control={
                <Checkbox
                  checked={form.data_types.includes(option.value)}
                  onChange={() => handleDataTypeToggle(option.value)}
                  size="small"
                />
              }
              label={option.label}
            />
          ))}
        </FormGroup>
      </Box>

      <StyledTextField 
        label="Reason for Deletion (optional)" 
        multiline 
        rows={2}
        placeholder="Help us understand why you're leaving (optional)"
        value={form.reason} 
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('reason', e.target.value)} 
      />

      <StyledTextField 
        label="Additional Information" 
        multiline 
        rows={2}
        placeholder="Any additional details or special requests"
        value={form.additional_info} 
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('additional_info', e.target.value)} 
      />

      <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
        <div ref={turnstileContainerRef} />
      </Box>

      {error && <Typography color="error" sx={{ fontFamily: '"Poppins", sans-serif', fontSize: '0.85rem' }}>{error}</Typography>}

      <Button 
        type="submit" 
        variant="contained" 
        disabled={isButtonDisabled}
        sx={{ 
          mt: 1, 
          py: 1.5, 
          fontFamily: '"Poppins", sans-serif', 
          fontWeight: 600,
          borderRadius: '8px',
          textTransform: 'none',
          fontSize: '0.95rem',
          boxShadow: '0 4px 12px rgba(255, 0, 84, 0.3)',
          background: 'linear-gradient(45deg, #FF0054, #662d91)',
          '&:hover': {
            boxShadow: '0 6px 14px rgba(255, 0, 84, 0.4)',
          }
        }}
      >
        {loading ? <CircularProgress size={20} color="inherit" /> : 'Submit Deletion Request'}
      </Button>

      <Typography variant="caption" sx={{ mt: 2, opacity: 0.7, fontFamily: '"Poppins", sans-serif', textAlign: 'center' }}>
        By submitting this form, you acknowledge that this action is permanent and cannot be reversed.
      </Typography>
    </Box>
  );
};

export default DeleteDataForm;






